import { ExternalLink, FileText, Link2, Plus, X } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { ApiError } from '../../api/errors';
import { useSearch } from '../../api/integrations';
import { useUserMap } from '../../api/org';
import { useAddAttachment, useAttachments, useDependency, useRemoveAttachment, useSubtasks, useTasksByIds } from '../../api/tasks';
import type { Task } from '../../api/types';
import { useShell } from '../../app/shell-context';
import { Avatar } from '../../components/ui/Avatar';
import { Button, IconButton } from '../../components/ui/Button';
import { SearchSelect } from '../../components/ui/Combobox';
import { Field, FormAlert, Input } from '../../components/ui/Field';
import { Popover } from '../../components/ui/Popover';
import { Skeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import { cn } from '../../lib/cn';
import { useDebounced } from '../../lib/hooks';
import { useTaskActions } from './TaskActions';
import { CompleteToggle, DueLabel, TaskKey } from './TaskBits';
import { AddTaskRowButton, QuickAddTask } from './QuickAdd';
import { useSession } from '../../app/session';
import { todayISO } from '../../lib/dates';

function SectionTitle({ title, count, action }: { title: string; count?: number; action?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <h3 className="text-meta font-semibold text-fg-2">
        {title}
        {count !== undefined && count > 0 && <span className="tnum ml-1.5 font-normal text-fg-3">{count}</span>}
      </h3>
      {action}
    </div>
  );
}

/** Рядок пов'язаної задачі: номер, назва (посилання), виконавець, строк. */
function RelatedRow({ task, onRemove, removeLabel }: { task: Task; onRemove?: () => void; removeLabel?: string }) {
  const shell = useShell();
  const actions = useTaskActions();
  const users = useUserMap();
  const assignee = task.assigneeId ? users.get(task.assigneeId) : undefined;
  return (
    <li className="group/r flex min-h-9 items-center gap-2.5 rounded-sm px-1.5 hover:bg-surface-2">
      <CompleteToggle task={task} onClick={() => actions.toggle(task)} />
      <TaskKey task={task} />
      <button
        type="button"
        onClick={() => shell.openTask(task.id)}
        className={cn('min-w-0 flex-1 truncate text-left text-body hover:underline', task.status === 'completed' && 'text-fg-3 line-through')}
      >
        {task.title}
      </button>
      {assignee && <Avatar name={assignee.name} src={assignee.avatarUrl} size={20} />}
      <DueLabel task={task} showEmpty={false} />
      {onRemove && (
        <IconButton label={removeLabel ?? 'Вилучити'} size="sm" onClick={onRemove} className="opacity-100 sm:opacity-0 sm:group-hover/r:opacity-100 sm:focus-visible:opacity-100">
          <X className="size-3.5" aria-hidden />
        </IconButton>
      )}
    </li>
  );
}

export function SubtasksSection({ task, canEdit = true }: { task: Task; canEdit?: boolean }) {
  const q = useSubtasks(task.id);
  const shell = useShell();
  const { userId } = useSession();
  const [adding, setAdding] = useState(false);
  const list = q.data ?? [];
  const open = list.filter((t) => t.status === 'open').length;
  const defaultDue = task.dueOn && task.dueOn >= todayISO() ? task.dueOn : undefined;
  return (
    <section aria-label="Підзадачі" data-tour="task-subtasks">
      <SectionTitle
        title="Підзадачі"
        count={list.length}
        action={
          canEdit && !adding ? (
            <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" aria-hidden />} onClick={() => setAdding(true)}>
              Додати підзадачу
            </Button>
          ) : undefined
        }
      />
      {q.isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : list.length ? (
        <>
          <ul className="flex flex-col">
            {list.map((t) => (
              <RelatedRow key={t.id} task={t} />
            ))}
          </ul>
          {list.length > 1 && (
            <p className="mt-1 px-1.5 text-caption text-fg-3">
              Завершено {list.length - open} з {list.length}
            </p>
          )}
        </>
      ) : !adding ? (
        <p className="px-1.5 text-meta text-fg-3">Розбийте роботу на кроки з окремими виконавцями й строками.</p>
      ) : null}
      {adding && (
        <QuickAddTask
          className="mt-1"
          placeholder="Назва підзадачі"
          defaults={{ parentId: task.id, assigneeId: task.assigneeId ?? userId, dueOn: defaultDue }}
          onClose={() => setAdding(false)}
          onFull={(d) => {
            setAdding(false);
            shell.openCreateTask({ parentId: task.id, parentKey: task.key, projectId: undefined, title: d.title || undefined, assigneeId: d.assigneeId ?? undefined, dueOn: d.dueOn ?? undefined });
          }}
        />
      )}
      {canEdit && !adding && list.length > 0 && <AddTaskRowButton label="Додати підзадачу" onClick={() => setAdding(true)} className="mt-0.5" />}
    </section>
  );
}

/** Залежності як рядок реквізитів: задачі, без яких цю не можна завершити. */
export function DependenciesRow({ task, disabled }: { task: Task; disabled?: boolean }) {
  const deps = useTasksByIds(task.dependsOnIds);
  const dep = useDependency();
  const toast = useToast();
  const shell = useShell();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 200);
  const search = useSearch(debounced);
  const options = (search.data?.tasks ?? [])
    .filter((t) => t.id !== task.id && !task.dependsOnIds.includes(t.id))
    .map((t) => ({ value: t.id, label: t.title, description: `${t.key}${t.status === 'completed' ? ' · завершена' : ''}` }));
  const remove = (id: string) =>
    dep.mutate({ id: task.id, dependsOnId: id, add: false }, { onError: (e) => toast.error('Залежність не вилучено', e instanceof ApiError ? e.detail : undefined) });
  return (
    <>
      {deps.map((d) => (
        <span key={d.id} className="inline-flex max-w-full items-center rounded-sm border border-line bg-surface text-meta">
          {d.task ? (
            <button
              type="button"
              onClick={() => shell.openTask(d.task!.id)}
              title={d.task.title}
              className={cn('flex min-w-0 items-center gap-1.5 py-1 pr-1.5 pl-2 text-left hover:underline', d.task.status === 'completed' && 'text-fg-3 line-through')}
            >
              <span className="key text-fg-3">{d.task.key}</span>
              <span className="max-w-[220px] truncate">{d.task.title}</span>
            </button>
          ) : (
            <span className="px-2 py-1 text-fg-3">{d.isLoading ? 'Завантаження…' : 'Задача недоступна'}</span>
          )}
          {!disabled && (
            <button
              type="button"
              aria-label={`Вилучити залежність${d.task ? ` від ${d.task.key}` : ''}`}
              onClick={() => remove(d.id)}
              className="inline-flex size-6 items-center justify-center border-l border-line text-fg-3 hover:bg-surface-2 hover:text-fg"
            >
              <X className="size-3" aria-hidden />
            </button>
          )}
        </span>
      ))}
      {!task.dependsOnIds.length && <span className="text-body text-fg-3">Немає</span>}
      {!disabled && (
        <IconButton label="Додати залежність" size="sm" onClick={(e) => setAnchor(e.currentTarget)}>
          <Plus className="size-4" aria-hidden />
        </IconButton>
      )}
      <SearchSelect
        open={!!anchor}
        onClose={() => {
          setAnchor(null);
          setQuery('');
        }}
        anchor={anchor}
        placement="bottom-start"
        options={options}
        value={null}
        label="Задача, на яку чекає ця"
        placeholder="Номер або назва задачі"
        emptyText={query.trim().length < 2 ? 'Введіть щонайменше 2 символи' : search.isFetching ? 'Пошук…' : 'Нічого не знайдено'}
        remoteFilter
        onQueryChange={setQuery}
        onSelect={(id) =>
          dep.mutate(
            { id: task.id, dependsOnId: id, add: true },
            { onSuccess: () => toast.success('Залежність додано'), onError: (e) => toast.error('Залежність не додано', e instanceof ApiError ? e.detail : undefined) },
          )
        }
      />
    </>
  );
}

export function AttachmentsSection({ task }: { task: Task }) {
  const q = useAttachments(task.id);
  const add = useAddAttachment(task.id);
  const remove = useRemoveAttachment(task.id);
  const toast = useToast();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const list = q.data ?? [];
  const isDrive = /^https:\/\/(drive|docs)\.google\.com\//.test(url.trim());
  const serverError = add.error instanceof ApiError ? add.error : null;

  return (
    <section aria-label="Вкладення">
      <SectionTitle
        title="Вкладення"
        count={list.length}
        action={
          <Button
            size="sm"
            variant="ghost"
            icon={<Plus className="size-3.5" aria-hidden />}
            onClick={(e) => {
              setName('');
              setUrl('');
              add.reset();
              setAnchor(e.currentTarget);
            }}
          >
            Додати
          </Button>
        }
      />
      {q.isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : list.length ? (
        <ul className="flex flex-col">
          {list.map((a) => (
            <li key={a.id} className="group/a flex min-h-9 items-center gap-2.5 rounded-sm px-1.5 hover:bg-surface-2">
              {a.kind === 'drive' ? <FileText className="size-4 shrink-0 text-fg-3" aria-hidden /> : <Link2 className="size-4 shrink-0 text-fg-3" aria-hidden />}
              <a href={a.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 truncate text-body text-fg underline decoration-line-strong underline-offset-2 hover:decoration-fg-2">
                {a.name}
              </a>
              <span className="hidden text-caption text-fg-3 sm:inline">{a.kind === 'drive' ? 'Google Drive' : 'Посилання'}</span>
              <ExternalLink className="size-3.5 shrink-0 text-fg-3" aria-hidden />
              <IconButton
                label={`Вилучити вкладення «${a.name}»`}
                size="sm"
                className="opacity-100 sm:opacity-0 sm:group-hover/a:opacity-100 sm:focus-visible:opacity-100"
                onClick={() => remove.mutate(a.id, { onError: (e) => toast.error('Вкладення не вилучено', e instanceof ApiError ? e.detail : undefined) })}
              >
                <X className="size-3.5" aria-hidden />
              </IconButton>
            </li>
          ))}
        </ul>
      ) : (
        <p className="px-1.5 text-meta text-fg-3">Посилання на документи в Google Drive чи інші джерела. Файли зберігаються в Drive, а не в ЄРС.</p>
      )}
      <Popover open={!!anchor} onClose={() => setAnchor(null)} anchor={anchor} placement="bottom-end" aria-label="Нове вкладення" className="w-[340px] max-w-[calc(100vw-16px)]">
        <form
          noValidate
          className="flex flex-col gap-3 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!/^https?:\/\/\S+\.\S+/i.test(url.trim())) {
              setUrlError('Вкажіть повну адресу, що починається з https://');
              return;
            }
            setUrlError(null);
            add.mutate(
              { name: name.trim() || url.trim(), url: url.trim(), kind: isDrive ? 'drive' : 'link' },
              {
                onSuccess: () => {
                  setAnchor(null);
                  toast.success('Вкладення додано');
                },
              },
            );
          }}
        >
          <Field label="Посилання" required error={urlError ?? serverError?.fieldError('url')} hint={isDrive ? 'Розпізнано файл Google Drive.' : 'Адреса https://…'}>
            <Input data-autofocus type="url" inputMode="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://drive.google.com/…" />
          </Field>
          <Field label="Назва" error={serverError?.fieldError('name')}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Наприклад: Довіреність (скан)" />
          </Field>
          {serverError && !serverError.fieldError('url') && !serverError.fieldError('name') && <FormAlert detail={serverError.detail} />}
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAnchor(null)}>
              Скасувати
            </Button>
            <Button size="sm" type="submit" variant="primary" loading={add.isPending} disabled={!url.trim()}>
              Додати
            </Button>
          </div>
        </form>
      </Popover>
    </section>
  );
}
